import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { createSectionTracker, trackEvent } from '../../services/funnelTracker';

interface SolutionSectionProps {
  onScrollToForm: () => void;
}

const solutionPoints = [
  {
    title: 'Proyecto real end-to-end',
    description: 'No ejercicios aislados. Construís un pipeline completo desde cero que va a tu portfolio.',
  },
  {
    title: 'SQL avanzado + Databricks',
    description: 'Las dos herramientas que más piden en entrevistas para Data Engineer. Las aprendés haciendo.',
  },
  {
    title: '10 semanas, 38h de cursada',
    description: '2h semanales de clase teórica en vivo (grabadas) + acceso permanente a la plataforma. Proyecto final con presentación incluida.',
  },
  {
    title: 'Acompañamiento directo conmigo',
    description: 'No con un tutor genérico. Yo corrijo tu código, respondo tus dudas, y te preparo para entrevistas.',
  },
];

export default function SolutionSection({ onScrollToForm }: SolutionSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tracker = createSectionTracker('solution_section');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();
  }, []);

  const handleApplyClick = () => {
    trackEvent('cta_apply_click', { source: 'solution_section' });
    onScrollToForm();
  };

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
          {/* Left: Visual element */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-green-600/20 to-blue-600/20 border border-green-500/30 rounded-3xl p-8 sm:p-10">
              {/* Before / After */}
              <div className="space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-widest text-red-400 font-bold">Antes</span>
                  <p className="text-gray-400 mt-1 text-sm line-through">
                    Cursos teóricos sin fin, sin portfolio, sin saber qué piden en entrevistas
                  </p>
                </div>
                <div className="flex justify-center">
                  <ArrowDown className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <span className="text-xs uppercase tracking-widest text-green-400 font-bold">Después del Bootcamp</span>
                  <p className="text-white mt-1 text-sm font-medium">
                    Proyecto real en tu portfolio, SQL avanzado dominado, listo para entrevistas de Data Engineer
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">10</div>
                  <div className="text-xs text-gray-400 mt-1">semanas</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">38h</div>
                  <div className="text-xs text-gray-400 mt-1">cursada + plataforma</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Text content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-green-400 mb-3">
              Lo que hago diferente
            </h2>
            <p className="text-gray-400 mb-8">
              Fui radiólogo. Hice la transición a Data Engineer y hoy trabajo remoto para una empresa de US. Este bootcamp es el camino que me hubiese ahorrado meses de frustración.
            </p>

            <div className="space-y-5">
              {solutionPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-semibold">{point.title}</h3>
                    <p className="text-gray-400 text-sm mt-0.5">{point.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              viewport={{ once: true }}
              className="mt-8"
            >
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                onClick={handleApplyClick}
              >
                Quiero aplicar
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
